import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, unref as _unref, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock, withCtx as _withCtx, resolveComponent as _resolveComponent, renderList as _renderList, Fragment as _Fragment, vShow as _vShow, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = {
  id: "edit-organization",
  class: "d-flex flex-fill edit-page-100"
}
const _hoisted_2 = { class: "card border-0 background-fb" }
const _hoisted_3 = { class: "edit-header" }
const _hoisted_4 = { class: "label" }
const _hoisted_5 = {
  id: "organization-draft",
  class: "card-body overflow-auto font-size-14"
}
const _hoisted_6 = { class: "display" }
const _hoisted_7 = /*#__PURE__*/_createElementVNode("span", { class: "color-red" }, "*", -1)
const _hoisted_8 = { class: "margin-top-10 d-flex flex-column" }
const _hoisted_9 = { class: "margin-top-10 d-flex flex-column" }
const _hoisted_10 = {
  key: 0,
  class: "margin-top-10 d-flex flex-column"
}
const _hoisted_11 = { class: "d-flex justify-content-end" }
const _hoisted_12 = { class: "d-flex flex-column" }
const _hoisted_13 = { class: "d-flex flex-column margin-top-10" }
const _hoisted_14 = { class: "d-flex flex-row justify-content-between align-items-center" }
const _hoisted_15 = ["onClick"]
const _hoisted_16 = { class: "card-footer footer" }
const _hoisted_17 = { class: "float-right" }
const _hoisted_18 = { class: "loader-div-40" }
const _hoisted_19 = ["src"]

import {
  ElButton,
  ElInput,
  ElOption,
  ElScrollbar,
  ElSelect,
  ElRow,
  ElCol,
  ElDivider,
  ElCheckbox,
  ElDialog,
} from "element-plus";
import { computed, onBeforeMount, ref } from "vue";
import { cloneDeep } from "lodash-es";
import { TYPE } from "vue-toastification";
import { currentRoute, router } from "@/core/router";
import { authInfo, hasPermission } from "@/core/auth";
import { getUsersList } from "@/user/services/user.service";
import { getRoleList } from "@/role/services/role.service";
import {
  addOrganization,
  editOrganization,
  getOrganization,
  checkConnections,
  isWorkspaceCreated,
} from "@/organization/services/organization.service";
import {
  getAssignableConnectorList,
  getExecutionsList,
} from "@/connector/services/connector.service";
import Transfer from "@/common/components/Transfer.vue";
import ExecutionsTransfer from "@/common/components/ExecutionsTransfer.vue";
import OrganizationLimitsSection from "@/organization/components/OrganizationLimitsSection.vue";
import { showAlert, showConfirm } from "@/core/popup";
import i18n from "@/core/i18n/i18n";
import { changeListItemOfBreadcrumb } from "@/core/layout";
import { ASSETS_BASE_URL, DEFAULT_LOGO } from "@/core/logo";
import { getOrganizationRoles } from "@/core/auth/user-role";
import { getTenantId } from "@/core/api";
import { getTenantByName } from "@/tenant/services/tenant.service";
import { getTenantDefault } from "@/flow/services/flow.service";
// Import the datastore refresh function and delete workspace service
import { refreshDataStorePlusMenu } from "@/datastore-plus";
import { deleteDataStorePlusWorkspace } from "@/datastore-plus/services/datastore-plus.service";

interface UserRoles {
  userId: string;
  roleId: string;
  old: boolean;
  oldId?: string;
}


export default /*#__PURE__*/_defineComponent({
  __name: 'EditOrganization',
  setup(__props) {

const { t } = i18n.global;
const pageName = computed(() =>
  currentRoute.value.name === "createOrganizations"
    ? t("Common.Create")
    : t("Common.Edit")
);

const users = ref([]);
const roles = ref([]);
const executionList = ref([]);

const id = ref("");

const isValid = computed(() => organization.value.name !== "");

const organizationRoles = ref<Array<UserRoles>>([]);

const organization = ref({
  id: "",
  name: "",
  connectors: [],
  executions: [],
  defaultExecution: "",
  connections: undefined,
  organizationRoles: [],
  dataStoreEnabled: false,
});

const connectorList = ref([]);

// Connection-optional connectors need no per-tenant configuration and are available in the flow
// editor tenant-wide regardless of organization assignment, so they're excluded from connectorList
// (via the /connectors/assignable endpoint) and, symmetrically, hidden here from the already-assigned
// side too - this list only ever contains connectors that are actually assignable.
const displayedOrganizationConnectors = computed(() => {
  const assignableSpecIds = new Set(
    (connectorList.value || []).map(
      (connector: any) => connector.connectorSpecId
    )
  );
  return (organization.value.connectors || []).filter((connector: any) =>
    assignableSpecIds.has(connector.connectorSpecId)
  );
});

const organizationSet = ref(false);
// OT-4982 §15: the limits section; null until it mounts (edit mode only).
const organizationLimitsSection = ref<InstanceType<
  typeof OrganizationLimitsSection
> | null>(null);

const pageLoading = ref(false);

const responseLoading = ref(false);

const executionsTransferKey = ref(0);
const dataStoreEnabled = ref(false);
const datastoreAssigned = ref(false);

// Add workspace state variables
const workspaceExists = ref(false);
const showDeleteWorkspaceConfirm = ref(false);

function updateConnectors(list) {
  organization.value.connectors = cloneDeep(list);
}

function updateExecutions(list) {
  organization.value.executions = cloneDeep(list);
}

function updateDefaultExecutions(item) {
  organization.value.defaultExecution = cloneDeep(item);
}
function addUserToOrganization() {
  organizationRoles.value.push({
    userId: "",
    roleId: "",
    old: false,
  });
}

function removeUserFromOrganization(index) {
  organizationRoles.value.splice(index, 1);
}

function isSelected(userId) {
  const index = organizationRoles.value.findIndex(
    (userRole) => userRole.userId === userId
  );
  return index !== -1;
}

function setUserAndRoles(): any {
  const organizationRoleList = [];
  organizationRoles.value.forEach((item) => {
    if (item.userId && item.roleId && !item.old) {
      const selectedItem = cloneDeep(
        users.value.find((user) => user.id === item.userId)
      );
      const role = cloneDeep(
        roles.value.find((roleItem) => roleItem.id === item.roleId)
      );
      if (selectedItem && role) {
        organizationRoleList.push({
          user: selectedItem,
          role,
        });
      }
    } else if (item.userId && item.roleId && item.old) {
      const selectedItem = cloneDeep(
        organization.value.organizationRoles.find(
          (orgRole) => orgRole.id === item.oldId
        )
      );
      if (selectedItem) {
        selectedItem.role = cloneDeep(
          roles.value.find((roleItem) => roleItem.id === item.roleId)
        );
        selectedItem.user = cloneDeep(
          users.value.find((userItem) => userItem.id === item.userId)
        );
        organizationRoleList.push(selectedItem);
      }
    }
  });
  return organizationRoleList;
}

async function emptyStringsToNullTopLevel(obj) {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return obj;
  Object.keys(obj).forEach((k) => {
    const v = obj[k];
    if (typeof v === "string" && v.trim() === "") obj[k] = null;
  });
  return obj;
}

async function saveOrganization() {
  responseLoading.value = true;
  let errorOccurred = false;

  if (
    organization.value.executions.length > 0 &&
    !organization.value.defaultExecution
  ) {
    showAlert(TYPE.WARNING, t("Common.DefaultExecutionRequired"));
    responseLoading.value = false;
    return;
  }
  // validate() warns about the first invalid changed limit itself.
  if (
    organizationLimitsSection.value &&
    !organizationLimitsSection.value.validate()
  ) {
    responseLoading.value = false;
    return;
  }
  if (isValid.value && hasPermission("ORGANIZATIONS.ORGANIZATION.SAVE")) {
    organization.value.organizationRoles = setUserAndRoles();

    // Add dataStoreEnabled flag to the payload for both edit and create
    if (id.value) {
      // For editing organization, we need to pass the dataStoreEnabled flag
      organization.value.dataStoreEnabled = dataStoreEnabled.value;
    } else {
      // For creating organization, we also need to pass the dataStoreEnabled flag
      organization.value.dataStoreEnabled = dataStoreEnabled.value;
    }

    if (id.value) {
      const usedConnectors = await checkConnections(organization.value);
      const isConfirmed = ref(true);
      if (usedConnectors) {
        isConfirmed.value = false;
        const message = `There are connections and flows connected to
                        ${usedConnectors} and they will be inactivated, do you confirm?`;
        isConfirmed.value = await showConfirm(
          "warning",
          t("Connector.DeleteConnector"),
          message
        );
      }
      if (isConfirmed.value) {
        let organizationSaved = false;
        await editOrganization(organization.value).then((response) => {
          if (response.code === 200) {
            organizationSaved = true;
            getOrganizationRoles(authInfo.value.userId);
            // Refresh datastore menu after successful save
            refreshDataStorePlusMenu();
          } else if (response.code === 409) {
            errorOccurred = true;
            showAlert(TYPE.ERROR, t("Organization.OrgAlreadyExists"));
          }
        });
        // OT-4982 §15: limits are written only after the organization saved; a failed write keeps
        // the user on the page.
        if (organizationSaved && organizationLimitsSection.value) {
          const limitsSaved = await organizationLimitsSection.value.save();
          if (!limitsSaved) {
            showAlert(TYPE.ERROR, t("OrganizationLimits.UpdateFailed"));
            errorOccurred = true;
          }
        }
        // Only once the limits result is known, so a failed limit write never follows a success.
        if (organizationSaved && !errorOccurred) {
          showAlert(TYPE.SUCCESS, t("Organization.OrgUpdated"));
        }
      }
    } else {
      organization.value = await emptyStringsToNullTopLevel(organization.value);
      await addOrganization(organization.value).then((response) => {
        if (response.code === 200) {
          showAlert(TYPE.SUCCESS, t("Organization.OrgCreated"));
          getOrganizationRoles(authInfo.value.userId);
          // Refresh datastore menu after successful save
          refreshDataStorePlusMenu();
        } else if (response.code === 409) {
          errorOccurred = true;
          showAlert(TYPE.ERROR, t("Organization.OrgAlreadyExists"));
        }
      });
    }
    const { fullPath } = currentRoute.value;
    if (
      fullPath.includes("organizations/create") ||
      fullPath.includes("organizations/edit")
    ) {
      if (!errorOccurred) {
        await router.push("/organizations");
      }
    }
  } else if (hasPermission("ORGANIZATIONS.ORGANIZATION.SAVE")) {
    showAlert(TYPE.WARNING, t("Common.WarningMsg"));
  }
  responseLoading.value = false;
}

// Function to handle workspace deletion
async function handleDeleteWorkspace() {
  responseLoading.value = true;

  try {
    const tenantId = await getTenantId();
    if (tenantId && id.value) {
      // Call the delete-workspace endpoint using service function
      const response = await deleteDataStorePlusWorkspace(id.value);

      if (response.code === 200) {
        // Success - hide dialog and update UI
        showDeleteWorkspaceConfirm.value = false;
        dataStoreEnabled.value = false;
        showAlert(TYPE.SUCCESS, t("Organization.WorkspaceDeleted"));
      } else {
        showAlert(
          TYPE.ERROR,
          response.message || t("Organization.WorkspaceDeleteFailed")
        );
      }
    }
  } catch (error) {
    console.error("Error deleting workspace:", error);
    showAlert(TYPE.ERROR, t("Organization.WorkspaceDeleteFailed"));
  } finally {
    responseLoading.value = false;
  }
}

onBeforeMount(async () => {
  pageLoading.value = true;
  await getUsersList().then((response) => {
    if (response) {
      users.value = response;
    }
  });

  await getRoleList(null).then((response) => {
    if (response) {
      roles.value = response;
    }
  });

  connectorList.value = (await getAssignableConnectorList()) ?? [];
  const tenantId = await getTenantId();
  const tenant = await getTenantByName(tenantId);
  executionList.value = tenant.executions;
  const tenantDefaultExecution = await getTenantDefault(tenantId);
  for (let i = 0; i < executionList.value.length; i++) {
    const execution = executionList.value[i];
    execution.isTenantDefault =
      execution.id === tenantDefaultExecution?.[0]?.execution?.id;
  }
  // Set datastoreAssigned flag from tenant
  datastoreAssigned.value = tenant.datastoreAssigned === true;
  id.value = currentRoute.value.params.id as string;

  if (id.value) {
    await getOrganization(id.value).then((response) => {
      if (response) {
        organization.value = cloneDeep(response);

        // Initialize dataStoreEnabled with the value from loaded organization
        dataStoreEnabled.value = organization.value.dataStoreEnabled ?? false;

        // Check if workspace exists for this organization
        // Moved await to proper async context
        const checkWorkspace = async () => {
          workspaceExists.value = await isWorkspaceCreated(id.value, tenantId);
        };
        checkWorkspace();

        const defaultExecutionItem = executionList.value.find(
          (execution) => execution.id === response?.defaultExecution?.id || null
        );
        executionsTransferKey.value++;
        if (defaultExecutionItem) {
          updateDefaultExecutions(defaultExecutionItem);
        }

        response.organizationRoles.forEach(async (organizationRole) => {
          organizationRoles.value.push({
            userId: organizationRole.user.id,
            roleId: organizationRole.role.id,
            old: true,
            oldId: organizationRole.id,
          });
        });
        organizationSet.value = true;
        changeListItemOfBreadcrumb({
          labelKey: "Common.Edit",
          route: "",
          disabled: true,
          isTranslated: false,
          uniqueLabel: organization.value.name,
        });
      }
    });
  }
  pageLoading.value = false;
});

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _withDirectives(_createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createElementVNode("div", _hoisted_4, _toDisplayString(pageName.value) + " " + _toDisplayString(_unref(t)("Common.Organization")), 1)
      ]),
      _createElementVNode("div", _hoisted_5, [
        _createVNode(_unref(ElRow), {
          gutter: 24,
          class: "two-col"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(ElCol), {
              xs: 24,
              lg: 11
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_6, [
                  _createElementVNode("strong", null, [
                    _createTextVNode(_toDisplayString(_unref(t)("Common.Name")), 1),
                    _hoisted_7
                  ]),
                  _createVNode(_unref(ElInput), {
                    modelValue: organization.value.name,
                    "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((organization.value.name) = $event)),
                    size: "small",
                    id: "organization-name",
                    placeholder: _unref(t)('Common.Name')
                  }, null, 8, ["modelValue", "placeholder"])
                ]),
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("strong", null, _toDisplayString(_unref(t)("Common.Connectors")), 1),
                  (!id.value || organizationSet.value)
                    ? (_openBlock(), _createBlock(Transfer, {
                        key: 0,
                        "source-name": "Connectors",
                        "target-name": "Organization Connectors",
                        items: connectorList.value,
                        "selected-items": displayedOrganizationConnectors.value,
                        onUpdate: updateConnectors,
                        "panel-height": '35vh'
                      }, null, 8, ["items", "selected-items"]))
                    : _createCommentVNode("", true)
                ]),
                _createElementVNode("div", _hoisted_9, [
                  (datastoreAssigned.value)
                    ? (_openBlock(), _createBlock(_unref(ElCheckbox), {
                        key: 0,
                        id: "create-workspace-checkbox",
                        modelValue: dataStoreEnabled.value,
                        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((dataStoreEnabled).value = $event)),
                        label: _unref(t)('Organization.DataStorePlusEnabled'),
                        size: "large"
                      }, null, 8, ["modelValue", "label"]))
                    : _createCommentVNode("", true)
                ]),
                (workspaceExists.value)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_10))
                  : _createCommentVNode("", true),
                (id.value && organizationSet.value)
                  ? (_openBlock(), _createBlock(OrganizationLimitsSection, {
                      key: 1,
                      ref_key: "organizationLimitsSection",
                      ref: organizationLimitsSection,
                      class: "margin-top-10",
                      "organization-id": id.value
                    }, null, 8, ["organization-id"]))
                  : _createCommentVNode("", true),
                _createVNode(_unref(ElDialog), {
                  modelValue: showDeleteWorkspaceConfirm.value,
                  "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((showDeleteWorkspaceConfirm).value = $event)),
                  title: _unref(t)('Organization.DeleteWorkspace'),
                  width: "30%"
                }, {
                  footer: _withCtx(() => [
                    _createElementVNode("div", _hoisted_11, [
                      _createVNode(_unref(ElButton), {
                        onClick: _cache[2] || (_cache[2] = ($event: any) => (showDeleteWorkspaceConfirm.value = false))
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_unref(t)("Common.Cancel")), 1)
                        ]),
                        _: 1
                      }),
                      _createVNode(_unref(ElButton), {
                        type: "danger",
                        onClick: handleDeleteWorkspace
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_unref(t)("Common.OK")), 1)
                        ]),
                        _: 1
                      })
                    ])
                  ]),
                  default: _withCtx(() => [
                    _createElementVNode("span", null, _toDisplayString(_unref(t)("Organization.ConfirmDeleteWorkspace")), 1)
                  ]),
                  _: 1
                }, 8, ["modelValue", "title"])
              ]),
              _: 1
            }),
            _createVNode(_unref(ElCol), {
              xs: 0,
              lg: 1,
              class: "col-divider-wrapper"
            }, {
              default: _withCtx(() => [
                _createVNode(_unref(ElDivider), {
                  direction: "vertical",
                  class: "col-divider"
                })
              ]),
              _: 1
            }),
            _createVNode(_unref(ElCol), {
              xs: 24,
              lg: 12
            }, {
              default: _withCtx(() => [
                _createElementVNode("div", _hoisted_12, [
                  _createElementVNode("strong", null, _toDisplayString(_unref(t)("Common.Executions")), 1),
                  (_openBlock(), _createBlock(ExecutionsTransfer, {
                    key: executionsTransferKey.value,
                    "target-name": _unref(t)('Common.Executions'),
                    items: executionList.value,
                    "selected-items": organization.value.executions,
                    "default-execution": organization.value.defaultExecution,
                    onUpdate: updateExecutions,
                    onDefaultSelected: updateDefaultExecutions
                  }, null, 8, ["target-name", "items", "selected-items", "default-execution"]))
                ]),
                _createElementVNode("div", _hoisted_13, [
                  _createElementVNode("div", _hoisted_14, [
                    _createElementVNode("strong", null, _toDisplayString(_unref(t)("Common.Users")), 1),
                    _createElementVNode("div", null, [
                      _createVNode(_unref(ElButton), {
                        size: "mini",
                        class: "btn-oneTeg",
                        id: "organization-add-user-role",
                        onClick: addUserToOrganization
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_font_awesome_icon, { icon: "plus" })
                        ]),
                        _: 1
                      })
                    ])
                  ]),
                  _createVNode(_unref(ElScrollbar), { height: '28vh' }, {
                    default: _withCtx(() => [
                      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(organizationRoles.value, (user, index) => {
                        return (_openBlock(), _createElementBlock("div", {
                          class: "d-flex flex-row justify-content-between margin-top-10 padding-right",
                          key: index
                        }, [
                          _createElementVNode("div", null, [
                            _createVNode(_unref(ElSelect), {
                              modelValue: user.userId,
                              "onUpdate:modelValue": ($event: any) => ((user.userId) = $event),
                              size: "small",
                              placeholder: _unref(t)('Organization.SelectUser'),
                              id: "organization-user-select",
                              class: "padding-sm"
                            }, {
                              default: _withCtx(() => [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(users.value.sort((a, b) =>
                          a.name.localeCompare(b.name)
                        ), (user, index) => {
                                  return (_openBlock(), _createBlock(_unref(ElOption), {
                                    key: user.id,
                                    label: 
                          user.name + (user.lastname ? ' ' + user.lastname : '')
                        ,
                                    value: user.id,
                                    id: 'organization-user-option-' + index,
                                    disabled: isSelected(user.id)
                                  }, null, 8, ["label", "value", "id", "disabled"]))
                                }), 128))
                              ]),
                              _: 2
                            }, 1032, ["modelValue", "onUpdate:modelValue", "placeholder"])
                          ]),
                          _createElementVNode("div", null, [
                            _createVNode(_unref(ElSelect), {
                              modelValue: user.roleId,
                              "onUpdate:modelValue": ($event: any) => ((user.roleId) = $event),
                              id: "organization-role-select",
                              class: "padding-sm",
                              size: "small",
                              placeholder: _unref(t)('Organization.SelectRole')
                            }, {
                              default: _withCtx(() => [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(roles.value, (role, index) => {
                                  return (_openBlock(), _createBlock(_unref(ElOption), {
                                    id: 'organization-role-option-' + index,
                                    key: role.id,
                                    label: role.authority,
                                    value: role.id
                                  }, null, 8, ["id", "label", "value"]))
                                }), 128))
                              ]),
                              _: 2
                            }, 1032, ["modelValue", "onUpdate:modelValue", "placeholder"])
                          ]),
                          _createElementVNode("div", null, [
                            _createElementVNode("button", {
                              class: "btn btn-link",
                              type: "button",
                              id: "organization-remove-user-role",
                              onClick: ($event: any) => (removeUserFromOrganization(index))
                            }, [
                              _createVNode(_component_font_awesome_icon, { icon: "trash" })
                            ], 8, _hoisted_15)
                          ])
                        ]))
                      }), 128))
                    ]),
                    _: 1
                  })
                ])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _createElementVNode("div", _hoisted_16, [
        _createElementVNode("div", _hoisted_17, [
          _createVNode(_unref(ElButton), {
            class: "btn-oneTeg3",
            id: "organization-back",
            onClick: _cache[4] || (_cache[4] = ($event: any) => (_unref(router).push('/organizations')))
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)("Common.Back")), 1)
            ]),
            _: 1
          }),
          (_unref(hasPermission)('ORGANIZATIONS.ORGANIZATION.SAVE'))
            ? (_openBlock(), _createBlock(_unref(ElButton), {
                key: 0,
                id: "organization-submit",
                class: "btn-oneTeg2",
                onClick: saveOrganization
              }, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_unref(t)("Common.Save")), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ])
      ])
    ], 512), [
      [_vShow, !pageLoading.value]
    ]),
    _withDirectives(_createElementVNode("div", _hoisted_18, [
      _createElementVNode("img", {
        src: `${_unref(ASSETS_BASE_URL)}/${_unref(DEFAULT_LOGO)}.png`,
        alt: "Loading...",
        class: "loader-logo-40"
      }, null, 8, _hoisted_19)
    ], 512), [
      [_vShow, responseLoading.value || pageLoading.value]
    ])
  ]))
}
}

})